import React, { ComponentType, ReactNode, forwardRef } from "react";

import { StyledTitle1 } from "./Styles";
import { Stack } from "../Stack";
import { Header2, Title2 } from "../Typography";

/** Heading typography of a TitledText — a component prop, not wire data. */
export type TitledTextVariant = "title1" | "title2" | "header2";

/** Props every heading in the trio accepts: content plus an optional anchor id. */
interface TitledTextHeadingProps {
  id?: string;
  children?: ReactNode;
}

const BoldTitle2 = ({ id, children }: TitledTextHeadingProps) => (
  <Title2 id={id} weight="bold">
    {children}
  </Title2>
);

/**
 * The one heading trio the envelope renders — TitledText variants and the
 * Portable Text `heading` / `titled-text` levels all resolve through this
 * map, so the typography can never drift between them.
 */
export const titledTextHeadings: Record<
  TitledTextVariant,
  ComponentType<TitledTextHeadingProps>
> = {
  title1: StyledTitle1,
  title2: BoldTitle2,
  header2: Header2,
};

export interface ITitledTextProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "title"> {
  title: string;
  variant?: TitledTextVariant;
}

/** A heading plus body, with typography control on the heading. */
export const TitledText = forwardRef<HTMLDivElement, ITitledTextProps>(
  ({ title, variant = "header2", children, ...rest }, ref) => {
    const Heading = titledTextHeadings[variant] ?? titledTextHeadings.header2;

    return (
      <Stack ref={ref} space={children ? "xs" : "none"} {...rest}>
        <Heading>{title}</Heading>
        {children}
      </Stack>
    );
  }
);

TitledText.displayName = "TitledText";
